import { MouseEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { RectangleButton } from 'us-react-components/dist/RectangleButton/RectangleButton';
import {
  JobAvailabilityType,
  JobType,
  ParentSignupUserReasonService,
  SignupReasonId
} from 'us-web-services';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Button, ButtonType } from '../../../components';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';

export const Frequency = observer((): JSX.Element => {
  const navigate = useNavigate();
  const { services: { tracker }, stores: { familyRegistration } } = useDependencies();
  const { t } = useTranslation();

  familyRegistration.setCurrentRoute(Routes.parent.registration.frequency);

  const getSignupReasonId = (availabilityTypeCode?: string): number => {
    switch (availabilityTypeCode) {
      case JobAvailabilityType.occasional:
        return SignupReasonId.ONE_TIME;
      case JobAvailabilityType.partTime:
        return SignupReasonId.PART_TIME;
      case JobAvailabilityType.fullTime:
        return SignupReasonId.FULL_TIME;
      default:
        return SignupReasonId.NOT_SURE;
    }
  }

  const updateParentSignupReason = async (availabilityTypeCode?: string): Promise<void> => {
    const data = {
      parentId: familyRegistration.profile.id,
      reason: {
        id: getSignupReasonId(availabilityTypeCode),
      },
    };

    await ParentSignupUserReasonService.create(data);
  }

  const navigateNextPage = async (): Promise<void> => {
    navigate(Routes.parent.registration.location);
  }

  const handleFrequencySelection = async (e: MouseEvent<HTMLButtonElement>, availabilityType?: JobAvailabilityType): Promise<void> => {
    familyRegistration.setJustBrowsing(false);
    familyRegistration.setStartDate(undefined);
    familyRegistration.setEndDate(undefined);
    familyRegistration.setRecurring({ days: [] });

    if (availabilityType) {
      familyRegistration.setAvailabilityType(availabilityType);
      familyRegistration.setJobType(JobType.RECURRING);
    } else {
      familyRegistration.setAvailabilityType(JobAvailabilityType.occasional);
      familyRegistration.setJobType(JobType.ONE_TIME);
    }

    const availability = availabilityType ?? JobAvailabilityType.occasional;
    await updateParentSignupReason(availability);

    const element = e.target as HTMLElement;

    const trackingProps = {
      elementText: element?.innerText,
      elementId: element?.id,
    };

    tracker.track('clicked-button', trackingProps);
    await navigateNextPage();
  }

  const handleJustBrowsing = async (): Promise<void> => {
    familyRegistration.setJustBrowsing(true);
    familyRegistration.setStartDate(undefined);
    familyRegistration.setEndDate(undefined);

    await updateParentSignupReason();
    await navigateNextPage();
  }

  const getPartTimeSubtitle = (): string => {
    if (familyRegistration.lookingForId === bookingVerticalType.CHILD_CARE) {
      return t('pages.nux.family.frequency.rectangleButtons.partTimeSubtitleChildcare');
    }
    return t('pages.nux.family.frequency.rectangleButtons.partTimeSubtitle');
  }

  const getFullTimeSubtitle = (): string => {
    if (familyRegistration.lookingForId === bookingVerticalType.CHILD_CARE) {
      return t('pages.nux.family.frequency.rectangleButtons.fullTimeSubtitleChildcare');
    }
    return t('pages.nux.family.frequency.rectangleButtons.fullTimeSubtitle');
  }

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={t('pages.nux.family.frequency.title')}
      hideContinue>
      <div className='flex flex-col w-[90%] md:w-[45%] pt-5 md:pt-10'>
        <RectangleButton
          className='mb-4'
          title={t('common.frequencies.specificDate')}
          subtitle={t('pages.nux.family.frequency.rectangleButtons.specificDateSubtitle')}
          onClick={(e): Promise<void> => handleFrequencySelection(e)}
        />
        <RectangleButton
          className='mb-4'
          title={t('common.frequencies.partTime')}
          subtitle={getPartTimeSubtitle()}
          onClick={(e): Promise<void> => handleFrequencySelection(e, JobAvailabilityType.partTime)}
        />
        <RectangleButton
          title={t('common.frequencies.fullTime')}
          subtitle={getFullTimeSubtitle()}
          onClick={(e): Promise<void> => handleFrequencySelection(e, JobAvailabilityType.fullTime)}
        />
        <div className='py-6 flex justify-center'>
          <Button
            buttonType={ButtonType.tertiary}
            onClick={(): Promise<void> => handleJustBrowsing()}
          >
            {t('pages.nux.family.frequency.justBrowsing')}
          </Button>
        </div>
      </div>
    </NuxContainer>
  )
});
